import { formatSingleDate, prefixClass } from 'lib/utils';
import './TransactionHeader.scss';
import { IconBlockerAlert, IconFlag, IconFlagFilled, IconKaiSynth, IconLink, IconPendoInfo } from 'components/Icons';
import { useParams } from 'react-router-dom';
import useFetchLinks from 'hooks/useFetchLinks';
import { AskRequest, commonUtil, EntityLink, ResolvedLink, StartEndDates } from '@hone-automation/common';
import { Tooltip, Whisper } from 'rsuite';
import { formatRangeValues } from '../../../../lib/reportUtils';
import { useQueuePanel } from '../common/QueuePanel';
import { StatusItem } from '../common/StatusItem';
import { STATUS_MAP } from '../TransactionDetails/TransactionDetails';
import { useTransactionDetails } from 'hooks/useTransactionDetails';
import classNames from 'classnames';
import FormURL from '../FormUrl';

import { getRequestHeaderStatus } from '../utils/askRequestUtils';

interface TransactionDataRowValues {
  titlePath: (string | null | undefined)[];
  date: StartEndDates;
  amount: string;
  setEnableFlagCellView: (value: boolean) => void;
  showFlagCellView: boolean;
  status: string;
  handleOpenTransactionDetails: () => void;
  handleOpenSummary: () => void;
}

interface TransactionHeaderProps {
  request?: AskRequest;
  dataRowValues?: TransactionDataRowValues;
}

function TransactionHeader({ request, dataRowValues }: TransactionHeaderProps) {
  const { locationId } = useParams();
  const { openPanel } = useQueuePanel();
  const prefix = prefixClass('transaction-header');

  // Always call hooks unconditionally to maintain consistent hook order
  const { data: links = [] } = useFetchLinks(request || null, locationId);
  const transactionDetails = useTransactionDetails(request || null);

  if (!request && !dataRowValues) return null;

  const {
    titlePath,
    date,
    amount,
    setEnableFlagCellView,
    showFlagCellView,
    status: dataRowStatus,
  } = dataRowValues || {};

  const linksEnabled = request?.type !== 'Customer Request';

  // Always show basic request information
  const hasLinks = linksEnabled && links && Array.isArray(links) && links.length > 0;
  const firstLink = hasLinks ? (links as ResolvedLink<EntityLink>[])[0] : null;
  const hasValidLink =
    firstLink && firstLink.valid && firstLink.sectionPath && firstLink.link && firstLink.dataDateRange;

  // Only set these values if we have a valid link
  let dataDateRange: StartEndDates | undefined;
  let validDate = false;
  let trxURL = '';
  let routePath: (string | undefined)[] = [];

  if (hasValidLink && firstLink && firstLink.dataDateRange && firstLink.link && firstLink.sectionPath) {
    dataDateRange = firstLink.dataDateRange;
    // Ensure dataDateRange has the required properties before using them
    if (dataDateRange && typeof dataDateRange.start === 'string') {
      validDate = commonUtil.validShortDate(dataDateRange.start);
      const trxURLPrefix = window.location.href.split('/app')[0];
      trxURL = `${trxURLPrefix}/app/location/${locationId}/report/${firstLink.link.toId}?flaggedTransaction=${firstLink.link.fromId}&fromType=${firstLink.link.fromType}`;
      routePath = [firstLink.reportTitle, ...firstLink.sectionPath.split('|')];
    }
  }

  const finalPath = routePath.length > 0 ? routePath : titlePath;

  const finalRequestStatus = request?.status;

  return (
    <div className={prefix()}>
      {/* Request identifier/title */}
      <div className={prefix('info')}>
        {/* Date range information - only if we have valid links */}

        {hasValidLink && dataDateRange ? (
          validDate ? (
            <div className={prefix('week')}>
              {dataDateRange.name && <span>{dataDateRange.name}</span>}
              {`${formatRangeValues(`${dataDateRange.start} - ${dataDateRange.end}`, 'not used')}`}
            </div>
          ) : (
            <div className={prefix('week')}>{formatSingleDate(dataDateRange.start)}</div>
          )
        ) : date ? (
          <div className={prefix('week')}>
            {date.name && <span>{date.name}</span>}
            {date.start && date.end
              ? formatRangeValues(`${date.start} - ${date.end}`, 'not used')
              : formatSingleDate(date.start)}
          </div>
        ) : null}

        {finalPath && finalPath.length > 0 ? (
          <FormURL routePath={finalPath} />
        ) : (
          <div className={prefix('title')}>{request?.identifier}</div>
        )}
      </div>
      {amount && <div className={prefix('amount')}>{amount}</div>}
      {/* Always show status */}
      <StatusItem
        userName={''}
        status={STATUS_MAP[finalRequestStatus || 'Pending']}
        date={transactionDetails?.updatedDate || ''}
        showDate
      />
      {showFlagCellView && setEnableFlagCellView && (
        <div className={classNames(prefix('flag'), { [dataRowStatus as string]: true })}>
          <Whisper speaker={<Tooltip>{!request ? 'Flag this cell' : 'Flagged cell'}</Tooltip>}>
            <button className={'flag'} onClick={() => setEnableFlagCellView(true)}>
              {request ? <IconFlagFilled /> : <IconFlag />}
            </button>
          </Whisper>
        </div>
      )}

      <div className={prefix('actions')}>
        {/* Always show priority if it's a blocker - using transactionDetails for type safety */}
        {transactionDetails?.hasBlockerPriority && (
          <Whisper trigger="hover" placement="top" speaker={<Tooltip>Urgent Request</Tooltip>}>
            <div className={prefix('icon-link blocker-alert')}>
              <IconBlockerAlert />
            </div>
          </Whisper>
        )}

        {request && (
          <Whisper placement="bottom" trigger="hover" delayOpen={400} speaker={<Tooltip>Create a Summary</Tooltip>}>
            <div
              className={prefix('icon-link')}
              onClick={() =>
                dataRowValues?.handleOpenSummary()
                  ? dataRowValues.handleOpenSummary()
                  : openPanel('summarizeRequest', {
                      data: { request, trxURL },
                      title: 'Request Summary',
                      titleIcon: <IconKaiSynth />,
                      showExplicitCloseButton: true,
                    })
              }
            >
              <IconKaiSynth />
            </div>
          </Whisper>
        )}

        {request && (
          <Whisper
            placement="left"
            trigger="hover"
            delayOpen={400}
            speaker={<Tooltip>View transaction details</Tooltip>}
          >
            <div
              className={prefix('icon-link')}
              onClick={() =>
                dataRowValues?.handleOpenTransactionDetails()
                  ? dataRowValues.handleOpenTransactionDetails()
                  : openPanel('transactionDetails', {
                      data: { request, trxURL },
                      title: 'Transaction Details',
                      titleIcon: <IconPendoInfo />,
                      showExplicitCloseButton: true,
                    })
              }
            >
              <IconPendoInfo />
            </div>
          </Whisper>
        )}
      </div>
    </div>
  );
}
export default TransactionHeader;
